/** Sentiment & tone analytics UI primitives.
 *
 *  SentimentChip: a dominant-sentiment status pill (positive / neutral
 *  / negative) plus a trend glyph.
 *  SentimentSparkline: hand-rolled inline SVG of net sentiment
 *  (positive_ratio - negative_ratio) over the last N windows.
 *  PersonSentimentBlock: self-contained fetch + render for the person
 *  detail slide-over (mirrors ImportantDatesBlock: takes personId,
 *  owns its data).
 *
 *  Sentiment is AI-derived per the inferred-vs-confirmed pattern, so
 *  the block renders with a dashed border until there is real data. */
import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { api, type SentimentTrendResponse, type SentimentWindow } from "../lib/api";

const TINT: Record<string, string> = {
  positive: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
  neutral: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  negative: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
};

export function SentimentChip({
  dominant,
  trend,
}: {
  dominant: string;
  trend?: string;
}) {
  const tint = TINT[dominant] ?? TINT.neutral;
  const TrendIcon =
    trend === "improving"
      ? TrendingUp
      : trend === "declining"
        ? TrendingDown
        : trend === "mixed"
          ? Activity
          : Minus;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${tint}`}
      title={trend ? `${dominant} · trend: ${trend}` : dominant}
    >
      <TrendIcon className="w-3 h-3" />
      {dominant}
    </span>
  );
}

export function SentimentSparkline({
  windows,
  width = 120,
  height = 28,
}: {
  windows: SentimentWindow[];
  width?: number;
  height?: number;
}) {
  if (windows.length === 0) return null;
  // Net sentiment in [-1, 1] mapped to y in [height, 0].
  const pts = windows.map((w, i) => {
    const net = w.positive_ratio - w.negative_ratio;
    const x = windows.length === 1 ? width / 2 : (i / (windows.length - 1)) * width;
    const y = height - ((net + 1) / 2) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = windows[windows.length - 1];
  const lastNet = last.positive_ratio - last.negative_ratio;
  const stroke =
    lastNet > 0.1 ? "#10b981" : lastNet < -0.1 ? "#f43f5e" : "#94a3b8";
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-label="Sentiment trend sparkline"
    >
      <line
        x1="0"
        y1={height / 2}
        x2={width}
        y2={height / 2}
        stroke="currentColor"
        strokeOpacity="0.15"
        strokeDasharray="2 2"
      />
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PersonSentimentBlock({ personId }: { personId: string }) {
  const [data, setData] = useState<SentimentTrendResponse | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .personSentimentTrend(personId, 12)
      .then((d) => alive && setData(d))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, [personId]);

  if (err || !data || data.windows.length === 0) return null;
  const latest = data.latest;
  return (
    <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            Sentiment trend
          </span>
          {latest && (
            <SentimentChip
              dominant={latest.dominant_sentiment}
              trend={latest.trend}
            />
          )}
        </div>
        <SentimentSparkline windows={data.windows} />
      </div>
      {latest && (
        <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          {Math.round(latest.positive_ratio * 100)}% positive ·{" "}
          {Math.round(latest.neutral_ratio * 100)}% neutral ·{" "}
          {Math.round(latest.negative_ratio * 100)}% negative · n=
          {latest.sample_count}
          {(latest.notable_topics?.length ?? 0) > 0 && (
            <span> · {(latest.notable_topics ?? []).slice(0, 3).join(", ")}</span>
          )}
        </div>
      )}
    </div>
  );
}
